import { useEffect } from 'react';
import { useVerifyAuthQuery } from '../features/auth/authApi';
import { useAppDispatch } from '../app/store';
import { setCredentials, setAuthInitialized } from '../store/authSlice';
import { coIntervueLogo } from '@assets/Icons';
import { FullscreenLoader } from '@cointervue/design-system/components';

/**
 * Initializes authentication state on app load
 * - Runs ONCE when app loads (or user refreshes)
 * - Only verifies auth if localStorage hints a session exists
 * - Sets isInitialized flag when done
 * - Uses RTK Query's built-in deduplication to prevent duplicate API calls
 * - No UI rendering - just auth initialization logic
 */
export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const hasSessionHint = localStorage.getItem('hasSession') === 'true';

  // RTK Query automatically deduplicates this call if multiple components try to verify
  // skip: true prevents automatic fetch if no session hint
  const { data, isSuccess, isError, isLoading } = useVerifyAuthQuery(undefined, {
    skip: !hasSessionHint,
  });

  useEffect(() => {
    if (!hasSessionHint) {
      dispatch(setAuthInitialized());
      return;
    }

    // Handle successful verification
    if (isSuccess && data?.success && data?.data?.user) {
      dispatch(
        setCredentials({
          user: {
            id: data.data.user.id,
            userEmail: data.data.user.email,
            fullName: data.data.user.fullName,
          },
          interviews: data.data.interviews,
          totalAllowedInterviews: data.data.totalAllowedInterviews,
        })
      );
      dispatch(setAuthInitialized());
      return;
    }

    // Handle verification failure
    if (isError || (data && !data.success)) {
      // Session expired or invalid, clear the hint
      localStorage.removeItem('hasSession');
      dispatch(setAuthInitialized());
    }
  }, [data, isSuccess, isError, hasSessionHint, dispatch]);

  if (hasSessionHint && isLoading) {
    return <FullscreenLoader logo={coIntervueLogo} />;
  }

  return <>{children}</>;
}

